import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useDataGridLocaleText } from 'hooks';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { FormattedMessage, useIntl } from 'react-intl';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { renderFormattedDate, renderDataGridLocaleText } from 'utils';
import { GridColDef, GridRowParams, GridToolbarProps } from '@mui/x-data-grid-premium';
import { ActivityBoardPermissionItem, PersonnelHolidayBalanceItem } from 'library/types';
import { DataGridBox, ZebraDataGrid, CustomToolbar, CustomNoRowsOverlay } from 'components';
import { Box, Card, Grid2 as Grid, TextField, Typography, InputAdornment, Tooltip } from '@mui/material';

// Type for the Master grid rows
type HolidayMonitoringUserRow = {
  id: number; // userId
  nameSurname: string;
  remainingHolidayCount: number;
  leaveCount: number;
  totalHours: number;
  leaveRows: ActivityBoardPermissionItem[];
};

type HolidayMonitoringToolbarProps = GridToolbarProps & {
  searchText?: string;
  searchPlaceholder?: string;
  onSearchTextChange?: (value: string) => void;
};

const HolidayMonitoringToolbar = ({
  searchText = '',
  searchPlaceholder = '',
  onSearchTextChange,
}: HolidayMonitoringToolbarProps) => (
  <CustomToolbar
    exportPremiumExcel
    singleFilter
    singleFilterComponent={
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={searchText}
        onChange={(event) => onSearchTextChange?.(event.target.value)}
        sx={{ width: 250, pb: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
    }
  />
);

const HolidayMonitoring = () => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const dataGridLocaleText = useDataGridLocaleText();

  // state hooks
  const [loading, setLoading] = useState<boolean>(false);
  const [rawData, setRawData] = useState<PersonnelHolidayBalanceItem[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  // Load leave activities
  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getPersonnelHolidayBalances();

      if (response.isSuccess) {
        setRawData(response.data || []);
      } else {
        enqueueSnackbar(
          response.message ||
            intl.formatMessage({
              id: 'personnelHoliday.loadError',
              defaultMessage: 'Veriler yüklenirken bir hata oluştu.',
            }),
          { variant: 'error' }
        );
      }
    } catch (err: any) {
      enqueueSnackbar(
        err?.message ||
          intl.formatMessage({
            id: 'personnelHoliday.loadError',
            defaultMessage: 'Veriler yüklenirken bir hata oluştu.',
          }),
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [intl, enqueueSnackbar]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  // Compute stats for KPI Cards
  const kpiStats = useMemo(() => {
    const uniqueUsers = new Set(rawData.map((item) => item.userId));
    const totalHours = rawData.reduce(
      (total, personnel) =>
        total + (personnel.holidays || []).reduce((sum, holiday) => sum + (holiday.estimatedHours || 0), 0),
      0
    );
    const totalLeaves = rawData.reduce((total, personnel) => total + (personnel.holidays || []).length, 0);

    return {
      uniqueUsersCount: uniqueUsers.size,
      totalHours,
      totalLeaves,
    };
  }, [rawData]);

  // Format data for Master row representation
  const userSummaryRows: HolidayMonitoringUserRow[] = useMemo(() => {
    const rows = rawData.map((personnel) => {
      const holidays = personnel.holidays || [];

      return {
        id: personnel.userId,
        nameSurname: personnel.nameSurname,
        remainingHolidayCount: personnel.remainingHolidayCount,
        leaveCount: holidays.length,
        totalHours: holidays.reduce((sum, holiday) => sum + (holiday.estimatedHours || 0), 0),
        leaveRows: holidays,
      };
    });

    // Apply search filter locally if search text exists
    if (searchText.trim()) {
      const lowerSearch = searchText.toLowerCase();

      return rows.filter((row) => row.nameSurname.toLowerCase().includes(lowerSearch));
    }

    return rows;
  }, [rawData, searchText]);

  // Columns for Master Grid (User List)
  const columns: GridColDef<HolidayMonitoringUserRow>[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({ id: 'holidayMonitoring.employee', defaultMessage: 'Personel' }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'remainingHolidayCount',
        headerName: intl.formatMessage({
          id: 'holidayMonitoring.remainingHolidayCount',
          defaultMessage: 'Kalan İzin Günü',
        }),
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        minWidth: 140,
        type: 'number',
      },
      {
        field: 'leaveCount',
        headerName: intl.formatMessage({ id: 'holidayMonitoring.leaveCount', defaultMessage: 'İzin Sayısı' }),
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        minWidth: 120,
        type: 'number',
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({ id: 'activityMonitoring.totalHours', defaultMessage: 'Toplam (saat)' }),
        align: 'center',
        headerAlign: 'center',
        flex: 0.6,
        minWidth: 120,
        type: 'number',
        valueFormatter: (value) => (value != null ? `${value} sa` : ''),
      },
    ],
    [intl]
  );

  // Columns for Detail Grid (Leave Details of a User)
  const detailColumns: GridColDef<ActivityBoardPermissionItem>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({ id: 'personnelHoliday.holidayType', defaultMessage: 'İzin Türü' }),
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => params.value || 'İzin',
      },
      {
        field: 'startDate',
        headerName: intl.formatMessage({ id: 'activityManaging.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        flex: 1.2,
        minWidth: 160,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
      {
        field: 'endDate',
        headerName: intl.formatMessage({ id: 'activityManaging.endDate', defaultMessage: 'Bitiş Tarihi' }),
        flex: 1.2,
        minWidth: 160,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
      },
      {
        field: 'estimatedHours',
        headerName: intl.formatMessage({ id: 'activityMonitoring.totalHours', defaultMessage: 'Toplam (saat)' }),
        flex: 0.6,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        valueFormatter: (value) => (value != null ? `${value} sa` : ''),
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'holidayMonitoring.description', defaultMessage: 'Açıklama' }),
        flex: 2,
        minWidth: 250,
      },
    ],
    [intl]
  );

  // Detail panel content generator callback
  const getDetailPanelContent = useCallback(
    (params: GridRowParams<HolidayMonitoringUserRow>) => {
      const row = params.row;
      if (!row.leaveRows || row.leaveRows.length === 0) return null;

      return (
        <Box sx={{ p: 2, backgroundColor: (theme) => (theme.palette.mode === 'dark' ? '#0d1117' : '#fafafa') }}>
          <ZebraDataGrid<ActivityBoardPermissionItem>
            columns={detailColumns}
            rows={row.leaveRows}
            getRowId={(r) => r.id}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [detailColumns, dataGridLocaleText]
  );

  // Detail panel height computer callback
  const getDetailPanelHeight = useCallback((params: GridRowParams<HolidayMonitoringUserRow>) => {
    const count = params.row.leaveRows?.length || 0;
    if (count === 0) return 0;
    // Row height in compact mode is ~36px, header ~56px, plus padding
    return count * 36 + 56 + 16;
  }, []);

  const toolbarProps: HolidayMonitoringToolbarProps = {
    searchText,
    searchPlaceholder: intl.formatMessage({
      id: 'holidayMonitoring.searchPlaceholder',
      defaultMessage: 'Personel Ara...',
    }),
    onSearchTextChange: setSearchText,
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* KPI 1: Unique Employees */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Tooltip
            title={intl.formatMessage({
              id: 'holidayMonitoring.kpiTooltip.employees',
              defaultMessage: 'İzin kaydı bulunan toplam benzersiz personel sayısı',
            })}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                boxShadow: (t) => (t.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                },
              }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  <FormattedMessage id="holidayMonitoring.totalEmployees" defaultMessage="Toplam Personel" />
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                  {loading ? '...' : kpiStats.uniqueUsersCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.08)',
                  color: 'primary.main',
                  p: 1.5,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <PeopleIcon fontSize="medium" />
              </Box>
            </Card>
          </Tooltip>
        </Grid>

        {/* KPI 2: Total Hours */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Tooltip
            title={intl.formatMessage({
              id: 'holidayMonitoring.kpiTooltip.hours',
              defaultMessage: 'Tüm personellerin toplam tahmini izin saati',
            })}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                boxShadow: (t) => (t.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                },
              }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  <FormattedMessage id="holidayMonitoring.totalHours" defaultMessage="Toplam İzin Saati" />
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                  {loading ? '...' : `${intl.formatNumber(kpiStats.totalHours, { maximumFractionDigits: 2 })} sa`}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                  color: 'success.main',
                  p: 1.5,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <AccessTimeIcon fontSize="medium" />
              </Box>
            </Card>
          </Tooltip>
        </Grid>

        {/* KPI 3: Total Requests */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Tooltip
            title={intl.formatMessage({
              id: 'holidayMonitoring.kpiTooltip.leaves',
              defaultMessage: 'Sistemde kayıtlı toplam izin aktivite sayısı',
            })}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                boxShadow: (t) => (t.palette.mode === 'dark' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.04)'),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                },
              }}>
              <Box>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  <FormattedMessage id="holidayMonitoring.totalLeaves" defaultMessage="Toplam İzin Talebi" />
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                  {loading ? '...' : kpiStats.totalLeaves}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)',
                  color: 'warning.main',
                  p: 1.5,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <ListAltIcon fontSize="medium" />
              </Box>
            </Card>
          </Tooltip>
        </Grid>
      </Grid>

      {/* DataGrid Premium Table */}
      <DataGridBox sx={{ height: '60vh' }}>
        <ZebraDataGrid<HolidayMonitoringUserRow>
          pagination
          columns={columns}
          rows={userSummaryRows}
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          disableRowSelectionOnClick
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: HolidayMonitoringToolbar,
          }}
          slotProps={{ toolbar: toolbarProps }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
        />
      </DataGridBox>
    </Box>
  );
};

export default HolidayMonitoring;
